/**
 * pages/reports.ts —— 双周报（W2-6）：列表 → 详情 → 编辑 三页式
 * 来历：一期 [SEC:page-reports] 的 rpListHtml / rpDetailHtml / rpEditHtml 与
 *      [SEC:page-reports-write] 的草稿 / 版本校验 / 提交 / 补交，渲染层重写、版式与交互不变。
 * 改版的唯一论证出处：`03_归档/09_双周报改版方案.md`。
 *
 * ## 三个路由（`router/parse.ts` 已支持，守卫已做三条改写）
 *   #/reports/p3            列表（第 3 期全部应交队伍）
 *   #/reports/p3/T01        详情（只读；期次轴 + 队伍轴双向切换）
 *   #/reports/p3/T01/edit   编辑（填写 / 补交 / 修订）
 *
 * ## 🔴 四条口径，改这个文件之前先读（写反了不会报错，只会悄悄对不上账）
 * 1. **判「交没交」一律看服务端下发的 `submitted`**，不判正文是否为空
 *    （三期加草稿后判空会把草稿正文漏出去）。
 * 2. **周期进行中不可能逾期**（截止 = 结束当日 24:00）。进行中只显示「距截止还有 N 天」，
 *    逾期只出现在【已结束】的期，且同一期里**全营天数相同**（页头出现一次即可）。
 * 3. **历史期补交不同步 KR**：`canSyncKr` 为 false 时不渲染 KR 区、**也不发 krSync**——
 *    带了服务端会 403 拒（前端不渲染不算权限）。
 * 4. **提交单向不可逆**：没有撤回 / 重新提交 / 确认 / 退回。提交后本队成员仍可继续编辑正文。
 *
 * ## ⚠ 二期不保存草稿（草稿归三期）
 * 草稿只活在 `state.rp.draft` 里，离开页面即丢 —— 所以**必须有离开拦截**
 * （`shared/events.ts` 的 `setLeaveGuard`，在 mounted 里按编辑态设置）。
 * 这是取消草稿换来的必做项，不是可选项。
 */
import type {
  KrProgressSyncItem,
  KrStatus,
  Period,
  ReportDetailView,
  ReportKrPick,
  ReportListRow,
  ReportListView,
  ReportMeta,
} from '@shared-types';
import { reports as api } from '../api/index.js';
import { ApiClientError, errText } from '../api/client.js';
import { confirmModal, emptyHtml, toast } from '../shared/common.js';
import { $, esc, when } from '../shared/dom.js';
import { setLeaveGuard } from '../shared/events.js';
import { EIC, IC } from '../shared/icons.js';
import * as meta from '../shared/meta.js';
import { state } from '../shared/state.js';
import { go } from '../router/index.js';
import { definePage } from './types.js';
import { krConflictModal } from './okr.js';

/** 状态四态。⚠ 与 OKR 页同一套文案，别在这里另起一份 */
const KRST: Record<KrStatus, string> = {
  idle: '未开始',
  doing: '进行中',
  done: '已完成',
  risk: '有风险',
};
const KRST_KEYS: readonly KrStatus[] = ['idle', 'doing', 'done', 'risk'];

const CYST: Record<string, string> = { future: '未开始', current: '进行中', ended: '已结束' };

/* ===================== 草稿 ===================== */

interface DraftKr {
  on: boolean;
  progress: number;
  status: KrStatus;
  /**
   * 打开编辑页那一刻的版本号。
   * 🔴 **必须是打开时那一版，不是渲染时的最新值**：它就是乐观锁的比对依据。
   *   每次渲染都刷成最新的话，「别人在你编辑期间改了这条 KR」永远检测不出来。
   */
  version: number;
}

export interface Draft {
  teamId: string;
  no: number;
  progress: string;
  plan: string;
  risk: string;
  need: string;
  krs: Record<string, DraftKr>;
}

/** 进编辑页时按服务端下发的当前值建草稿；上次同步过的 KR 默认勾上 */
export function newDraft(v: ReportDetailView): Draft {
  const krs: Record<string, DraftKr> = {};
  // ⚠ canSyncKr 为 false 时 v.krs 恒为空数组 → 这里天然是空对象，提交时也就不会带 krSync
  for (const k of v.krs) {
    krs[k.krId] = { on: k.lastSynced, progress: k.progress, status: k.status, version: k.version };
  }
  return {
    teamId: v.team.id,
    no: v.period.no,
    progress: v.report?.progress ?? '',
    plan: v.report?.plan ?? '',
    // ⚠ 「本期无。」是服务端补的占位，回填到输入框里会逼用户先删掉它才能写
    risk: unfill(v.report?.risk),
    need: unfill(v.report?.need),
    krs,
  };
}

const unfill = (s: string | undefined): string => (s === '本期无。' ? '' : (s ?? ''));

const draftKey = (teamId: string, no: number): string => `${teamId}|${no}`;
const draftOf = (): Draft | null => (state.rp.draft as Draft | null) ?? null;
const isDirty = (): boolean => !!state.rp.draft && JSON.stringify(state.rp.draft) !== state.rp.base;

function setDraft(d: Draft | null): void {
  state.rp.draft = d;
  state.rp.base = d ? JSON.stringify(d) : '';
  state.rp.dkey = d ? draftKey(d.teamId, d.no) : '';
}

/**
 * 判「交没交」的【唯一判据】：`status === 'submitted'`，**不判 `report` 是否为 null、
 * 更不判正文是否为空**（RULES §三）。
 *
 * ⚠ 这不是防御性写法：`report` 表里**真的存在 `status='unsubmitted'` 的行**
 *   （seed 就会造，三期加草稿后更是常态）。判 `report !== null` 会把这种行当成已提交，
 *   于是详情页把没交的正文当成果展示、编辑页把首次提交走成"修订"——联调时踩到过。
 */
const isSubmitted = (v: ReportDetailView): boolean => v.report?.status === 'submitted';

/* ===================== 渲染（纯函数） ===================== */

/** 提交时间：'2026-08-01 20:35'（秒没有意义，砍掉）。⚠ 不许 new Date()，字符串原样切 */
const fmtAt = (s: string | null): string => (s ? s.slice(0, 16) : '—');
/** 列表「最近编辑」第二行：'07-31 20:35' */
const fmtShort = (s: string | null): string => (s ? s.slice(5, 16) : '—');

/**
 * 周期切换器：列表页页头与详情页面包屑共用。
 * ⚠ 下拉里【不带任何统计】（2026-08-05 晚评审删掉了「（欠 N）」）：
 *   它是**导航控件**，职责是让人找到要去的那一期；把统计塞进选项会让每行变长，
 *   且与切过去之后页头副标题里的同一个数字重复。欠交支数只在副标题里出现一次。
 * @param suffix 切期后要保留的后缀（详情页传队伍号，切期不跳走）
 */
export function cycPickHtml(periods: Period[], today: string, cur: number | null, suffix: string): string {
  const list = periods.slice().sort((a, b) => a.no - b.no);
  if (!list.length) return '';
  const sfx = suffix ? '|' + suffix : '';
  const i = list.findIndex((p) => p.no === cur);
  const prev = i > 0 ? (list[i - 1] as Period) : null;
  const next = i >= 0 && i < list.length - 1 ? (list[i + 1] as Period) : null;
  /* ⚠ 这里判三态**只为下拉选项上的一个字**，不是口径：真正的状态由服务端下发
     （`ReportListView.state` / `ReportDetailView.state`）。日期比较用后端下发的
     'YYYY-MM-DD' 字符串，字典序即时间序，**不许 new Date()**（前端纪律 3）。 */
  const stOf = (p: Period): string =>
    !today ? '' : p.end < today ? 'ended' : p.start > today ? 'future' : 'current';
  const opt = (p: Period): string =>
    `<option value="${p.no}"${p.no === cur ? ' selected' : ''}>${esc(p.name)} · ${esc(
      p.start.slice(5),
    )}~${esc(p.end.slice(5))} · ${esc(CYST[stOf(p)] ?? '')}</option>`;

  return `<div class="rp-cyc">
    <button class="navbtn" data-rp="cyc|${prev ? prev.no : ''}${sfx}" ${prev ? '' : 'disabled'}
      title="${prev ? esc(prev.name) : '已是最早一期'}" aria-label="上一期">${IC.left}</button>
    <select id="rp-cycsel" data-rpsfx="${esc(suffix)}" aria-label="切换双周报周期">${list
      .map(opt)
      .join('')}</select>
    <button class="navbtn" data-rp="cyc|${next ? next.no : ''}${sfx}" ${next ? '' : 'disabled'}
      title="${next ? esc(next.name) : '已是最后一期'}" aria-label="下一期">${IC.right}</button>
  </div>`;
}

/**
 * 页头副标题：三种期次状态三套口径，**不能写成一句通用文案**。
 * 🔴 进行中一律不出现「逾期」二字（红线 3）。
 */
export function subHtml(v: ReportListView): string {
  const p = v.period;
  if (!p) return '暂无双周报周期';
  const base = `${esc(p.name)} · <span class="num">${esc(p.start.slice(5))} ~ ${esc(p.end.slice(5))}</span>`;
  if (v.state === 'future') return base + ' · 尚未开始';
  const rate = ` · 已提交 <span class="num">${v.rate.submitted} / ${v.rate.expected}</span>`;
  if (v.state === 'current') {
    const d = v.daysToDue;
    return (
      base +
      ' · 进行中' +
      rate +
      (d === null ? '' : d === 0 ? ' · 今天截止' : ` · 距截止还有 <span class="num">${d}</span> 天`)
    );
  }
  return (
    base +
    ' · 已结束' +
    rate +
    (v.owedTeams && v.owedDays > 0
      ? ` · 逾期未补交 <span class="num">${v.owedTeams}</span> 支（已逾期 <span class="num">${v.owedDays}</span> 天）`
      /* ⚠ 宽限日（结束后第一天）是非正式习惯：只写「未提交」，**不写原因、不写「不算逾期」**（用户 2026-10-07） */
      : v.owedTeams
        ? ` · 未提交 <span class="num">${v.owedTeams}</span> 支`
        : '')
  );
}

/** 状态单元格三态。⚠ 逾期只在【已结束的期】出现，且全营同一个天数 */
function stCellHtml(r: ReportListRow, v: ReportListView): string {
  if (r.submitted) return `<span class="rp-dot" aria-hidden="true"></span>已提交`;
  if (v.owedDays > 0) {
    return `<span class="rp-dot od" aria-hidden="true"></span>未提交 · <em>逾期 ${v.owedDays} 天</em>`;
  }
  return `<span class="rp-dot un" aria-hidden="true"></span>未提交`;
}

/** 队名后的两个文字标识。⚠ 判据在服务端（`shared/report.ts` 的 hasRisk / hasNeed），这里只照着下发的布尔值画 */
function flagsHtml(r: { hasRisk: boolean; hasNeed: boolean }): string {
  return (
    (r.hasRisk ? '<span class="flag">有风险</span>' : '') +
    (r.hasNeed ? '<span class="flag need">需支持</span>' : '')
  );
}

/**
 * 上一期横幅（2026-09-28）：只给管理员看，固定显示。
 * ⚠ 为什么要有：队伍常在下一期开头才补交，风险 / 诉求落在上一期，
 *   管理员默认停在本期，不切回去就看不到。服务端只在看【进行中】的期时下发 `prevAlert`。
 * ⚠ 横幅只是提醒，**不做已读 / 已处理**——那需要存状态，且容易被当成对双周报的「确认」（红线 3）。
 */
export function prevAlertHtml(v: ReportListView, d: ReportsData): string {
  const a = v.prevAlert;
  if (!a || !d.isAdmin) return '';
  const owed = a.expected - a.submitted;
  const owedTx = owed > 0 ? `；另有 <span class="num">${owed}</span> 支未提交，内容未知` : '';
  const pn = esc(a.period.name);
  const jump = `<button class="tbtn" data-rp="cyc|${a.period.no}">查看${pn}</button>`;
  if (!a.teams.length) {
    return `<div class="note mute rp-prev">${IC.info}<span>上一期（${pn}）已提交的双周报里，没有队伍写风险或资源诉求${owedTx}。</span>${jump}</div>`;
  }
  const chips = a.teams
    .map(
      (x) =>
        `<button class="rp-prev-tm" data-nav="#/reports/p${a.period.no}/${esc(x.team.id)}"><i class="no">${esc(
          x.team.code,
        )}</i>${esc(x.team.name)}${flagsHtml(x)}</button>`,
    )
    .join('');
  return `<div class="note rp-prev hit">${IC.info}<div class="rp-prev-bd"><span>上一期（${pn}）有 <span class="num">${a.teams.length}</span> 支队伍写了风险或资源诉求${owedTx}：</span><div class="rp-prev-tms">${chips}</div></div>${jump}</div>`;
}

/**
 * 列表一行。
 * ⚠ 【没有行内展开全文】（2026-08-05 晚评审删掉）：四段全文有专门的详情页，
 *   列表再放一份就是同一内容的两个入口，展开态还会破坏「一屏扫完全部队伍」。
 *   收起态两列各截断 2 行（`.c-tx` 的 line-clamp）。
 */
function rowHtml(r: ReportListRow, v: ReportListView): string {
  const op = r.submitted
    ? `<button class="tbtn" data-rp="open|${esc(r.team.id)}">查看</button>`
    : r.canEdit
      ? `<button class="tbtn" data-rp="edit|${esc(r.team.id)}">${v.state === 'ended' ? '补交' : '填写'}</button>`
      : `<button class="tbtn" data-rp="open|${esc(r.team.id)}">查看</button>`;
  const cell = (s: string | null): string =>
    s ? `<div class="c-tx">${esc(s)}</div>` : '<span class="dash">—</span>';

  return `
  <tr data-rp="open|${esc(r.team.id)}" tabindex="0"
    aria-label="${esc(r.team.name)}，${r.submitted ? '已提交' : '未提交'}">
    <td class="c-tm"><b><i class="no">${esc(r.team.code)}</i>${esc(r.team.name)}${
      flagsHtml(r)
    }</b><s>${esc(r.team.topic)}</s></td>
    <td><span class="c-st">${stCellHtml(r, v)}</span></td>
    <td>${cell(r.progress)}</td>
    <td>${cell(r.plan)}</td>
    <td class="c-ed">${
      r.meta
        ? esc(r.meta.lastEditName ?? r.meta.submitterName ?? '—') +
          '<br>' +
          esc(fmtShort(r.meta.lastEditAt ?? r.meta.submitAt))
        : '<span class="dash">—</span>'
    }</td>
    <td>${op}</td>
  </tr>`;
}

function tableHtml(v: ReportListView, d: ReportsData): string {
  if (!v.period) {
    return emptyHtml(
      EIC.cal,
      '还没有任何双周报周期',
      '由项目组管理员在管理后台创建第一期',
      d.isAdmin ? '<button class="tbtn" data-nav="#/admin/periods">去管理后台</button>' : '',
    );
  }
  if (v.state === 'future') {
    const back = d.backNo;
    return emptyHtml(
      EIC.cal,
      v.period.name + '尚未开始',
      `${v.period.start} ~ ${v.period.end}，周期开始后各队方可填报`,
      back == null ? '' : `<button class="tbtn" data-rp="cyc|${back}">回到第 ${back} 期</button>`,
    );
  }
  if (!v.rows.length) {
    return emptyHtml(
      EIC.team,
      '该期没有应提交的队伍',
      '已归档队伍已退出考核，其历史双周报仍可在队伍详情中查看',
      d.isAdmin ? '<button class="tbtn" data-nav="#/teams">去队伍管理</button>' : '',
    );
  }
  /* ⚠ 表头【不带排序按钮】：次序固定为队伍编号升序（服务端已排好），
     理由见 09 方案 §十一——编号是管理员唯一稳定的心智锚点。 */
  return `
  <table class="matrix rt">
    <colgroup><col class="rg-team"><col class="rg-st"><col class="rg-pg">
      <col class="rg-pl"><col class="rg-ed"><col class="rg-op"></colgroup>
    <thead><tr>
      <th>队伍</th><th>状态</th>
      <th>本期进展与成果</th><th>下期计划</th><th>最近编辑</th><th>操作</th>
    </tr></thead>
    <tbody>${v.rows.map((r) => rowHtml(r, v)).join('')}</tbody>
  </table>`;
}

export function listHtml(v: ReportListView, d: ReportsData): string {
  /* ⚠ 页头与表格之间【没有工具条】：排序、展开全文、"当前为管理员视角"三样
     已在 2026-08-05 晚的评审里去掉，不要"顺手加回来"。 */
  return `
  <div class="page">
    <div class="pagehead">
      <div>
        <h1>双周报</h1>
        <p class="ph-sub" id="rp-sub">${subHtml(v)}</p>
      </div>
      <div class="ph-act">${cycPickHtml(d.periods, d.today, v.period?.no ?? null, '')}</div>
    </div>

    ${when(
      v.noCurrentPeriod && !!v.period,
      `<div class="note mute" style="margin-top:14px">${IC.info}<span>当前没有进行中的周期，显示的是最近一期已结束的周期。新一期由项目组管理员在管理后台创建。</span>${
        d.isAdmin ? '<button class="tbtn" data-nav="#/admin/periods">去创建</button>' : ''
      }</div>`,
    )}

    ${prevAlertHtml(v, d)}

    <div class="panel" id="rp-panel" style="margin-top:16px">${tableHtml(v, d)}</div>
  </div>`;
}

/* ---------- 详情页 ---------- */

/** 详情页与编辑页共用的对象头 */
function headHtml(v: ReportDetailView, editing: boolean): string {
  const tag = isSubmitted(v)
    ? '<span class="rp-st sub">已提交</span>'
    : `<span class="rp-st un">未提交${
        v.state === 'ended' && v.owedDays ? ` · 逾期 ${v.owedDays} 天` : ''
      }</span>`;
  return `
  <div class="okr-head">
    <div class="okr-hm">
      <h2><span class="no num">${esc(v.team.code)}</span>${esc(v.team.name)}<span class="rp-cst ${
        v.state === 'current' ? 'cur' : v.state === 'ended' ? 'end' : 'fut'
      }">${esc(v.period.name)} · ${esc(CYST[v.state] ?? '')}</span>${tag}</h2>
      <p title="${esc(v.team.topic)}">${esc(v.team.topic)}${when(v.team.archived, ' ｜ <em>该队伍已归档</em>')}</p>
    </div>
    <!-- 未提交时这里【不放按钮】：下方空态已经带了「填写 / 补交」主按钮，
         两个同义按钮并排会让人以为是两个不同的动作 -->
    <div class="rp-hr">${
      editing || !isSubmitted(v) || !v.canEdit
        ? ''
        : `<button class="btn" data-rp="edit|${esc(v.team.id)}">编辑</button>`
    }</div>
  </div>`;
}

/**
 * KR 同步记录（只读）。
 * ⚠ 它是**历史留痕，不参与任何计算**——团队进度永远只由 KR 的当前 progress 算出，
 *   绝不能反过来用它去推。历史期恒为空，此时整块不渲染，**也不显示「暂无」占位**。
 */
function syncHtml(v: ReportDetailView): string {
  if (!v.krSync.length) return '';
  return `
  <div class="rp-card">
    <div class="rp-card-h"><b>本期同步的关键结果</b><em>提交时写入的历史记录，不参与进度计算</em></div>
    <div class="rp-sync">${v.krSync
      .map(
        (x) => `<div class="rp-sy"><i>${x.krNo === null ? '—' : 'KR' + x.krNo}</i>
        <b>${esc(x.krTitle ?? '（该关键结果已删除）')}</b>
        <s><span class="num">${x.fromProgress}%</span> · ${esc(KRST[x.fromStatus])} →
          <u><span class="num">${x.toProgress}%</span> · ${esc(KRST[x.toStatus])}</u></s></div>`,
      )
      .join('')}</div>
  </div>`;
}

/**
 * 提交信息行。
 * ⚠ `overdueDays` 是**提交那一刻的快照**，只有跨期补交才 > 0，改周期配置不重算；
 *   它与页头现算的逾期天数**不是一回事**，两者不要混（红线 3）。
 */
function metaHtml(m: ReportMeta | null): string {
  if (!m) return `<p class="rp-meta mute">提交后这里会记录创建人、参与编辑人员、最近编辑人与提交时间。</p>`;
  const bits: string[] = [];
  if (m.authorName) bits.push('创建人 ' + esc(m.authorName));
  if (m.editorNames.length) bits.push('参与编辑 ' + m.editorNames.map(esc).join('、'));
  if (m.lastEditAt) {
    bits.push('最近编辑 ' + (m.lastEditName ? esc(m.lastEditName) + ' · ' : '') + esc(fmtAt(m.lastEditAt)));
  }
  bits.push(
    '提交人 ' +
      esc(m.submitterName ?? '—') +
      ' · 提交于 ' +
      esc(fmtAt(m.submitAt)) +
      (m.overdueDays ? ` <em>逾期 ${m.overdueDays} 天提交</em>` : ''),
  );
  return `<p class="rp-meta">${bits.map((x) => '<span>' + x + '</span>').join('')}</p>`;
}

/** 四段正文单段字数上限，与后端 `reports/service.ts` 的 `SECTION_MAX` 一致，改动需两处同步 */
const SEC_MAX = 2000;

const SECS: ReadonlyArray<readonly ['progress' | 'plan' | 'risk' | 'need', string]> = [
  ['progress', '本期进展与成果'],
  ['plan', '下期计划'],
  ['risk', '问题与风险'],
  ['need', '资源诉求'],
];

export function detailHtml(v: ReportDetailView, d: ReportsData): string {
  const crumb = `
  <div class="crumb">
    <div class="crumb-path">
      <button data-rp="list">双周报</button><span>/</span>
      <button data-rp="list">${esc(v.period.name)}</button><span>/</span><b>${esc(v.team.name)}</b>
    </div>
    <div class="crumb-tools">
      ${cycPickHtml(d.periods, d.today, v.period.no, v.team.id)}
      <span class="gapline" aria-hidden="true"></span>
      <button class="navbtn" data-rp="team|${esc(v.nav.prevTeamId ?? '')}" ${
        v.nav.prevTeamId ? '' : 'disabled'
      }>${IC.left} 上一队</button>
      <span class="navpos num">${v.nav.index || '—'} / ${v.nav.total}</span>
      <button class="navbtn" data-rp="team|${esc(v.nav.nextTeamId ?? '')}" ${
        v.nav.nextTeamId ? '' : 'disabled'
      }>下一队 ${IC.right}</button>
      <button class="navbtn" data-rp="list">返回列表（Esc）</button>
    </div>
  </div>`;

  /* 未提交的三种空态：进行中 / 已结束 / 未开始，各自的按钮不一样。
     ⚠ 判据是 `status === 'submitted'`，不是 `report !== null`——库里存在未提交的行 */
  if (!isSubmitted(v)) {
    let title: string;
    let desc: string;
    let act = '';
    if (v.state === 'future') {
      title = v.period.name + '尚未开始';
      desc = `${v.period.start} ~ ${v.period.end}，周期开始后方可填报`;
      act = `<button class="tbtn" data-rp="list">返回列表</button>`;
    } else if (v.state === 'ended') {
      title = `${v.period.name}双周报未提交，已逾期 ${v.owedDays} 天`;
      desc = `截止时间为 ${v.period.end} 24:00。截止后仍可补交，系统会保留逾期标识与时长。`;
      if (v.canEdit) act = `<button class="btn primary" data-rp="edit|${esc(v.team.id)}">补交${esc(v.period.name)}</button>`;
    } else {
      const d = v.daysToDue;
      title = v.period.name + '双周报尚未提交';
      desc = `本期截止 ${v.period.due}。${d === null ? '' : d === 0 ? '今天截止。' : `距截止还有 ${d} 天。`}`;
      if (v.canEdit) {
        act = `<button class="btn primary" data-rp="edit|${esc(v.team.id)}">填写${esc(v.period.name)}双周报</button>`;
      }
    }
    return (
      crumb +
      `<div class="page">
        ${headHtml(v, false)}
        <div class="rp-doc">${emptyHtml(EIC.doc, title, desc, act)}</div>
      </div>`
    );
  }

  const r = v.report as NonNullable<ReportDetailView['report']>;
  return (
    crumb +
    `
  <div class="page">
    ${headHtml(v, false)}
    <div class="rp-doc">
      ${SECS.map(([k, lab]) => `<div class="rp-dsec"><h3>${lab}</h3><p>${esc(r[k] || '—')}</p></div>`).join('')}
    </div>
    ${syncHtml(v)}
    ${metaHtml(r)}
  </div>`
  );
}

/* ---------- 编辑页 ---------- */

/** 勾选后那一行的提示：提交时会把 KR 从什么改成什么 */
export function krDiff(k: ReportKrPick, c: DraftKr): string {
  // ⚠ 单向联动 status='done' → 100 由**服务端**补齐；这里只是把结果先说清楚，不代替服务端算
  const p = c.status === 'done' ? 100 : c.progress;
  return p === k.progress && c.status === k.status
    ? '与当前值一致，提交后不产生变化'
    : `提交时将由 ${k.progress}% · ${KRST[k.status]} 更新为 ${p}% · ${KRST[c.status]}`;
}

export function krsHtml(v: ReportDetailView, d: Draft): string {
  if (!v.krs.length) {
    return emptyHtml(
      EIC.flag,
      '该队尚未创建目标，本期无可关联的 KR',
      '建好 O 与 KR 后，双周报提交时可一并同步它们的进度与状态',
      `<button class="tbtn" data-rp="gokr">去团队 OKR 建立</button>`,
      'sm',
    );
  }
  // 服务端已按队内 seq 排好，这里只按 O 分组，不再排一次
  const groups: Array<{ id: string; no: number; title: string; krs: ReportKrPick[] }> = [];
  for (const k of v.krs) {
    let g = groups.find((x) => x.id === k.objectiveId);
    if (!g) {
      g = { id: k.objectiveId, no: k.objectiveNo, title: k.objectiveTitle, krs: [] };
      groups.push(g);
    }
    g.krs.push(k);
  }

  return groups
    .map(
      (g) => `<div class="rp-og">
      <div class="rp-ogh"><i class="en">O${g.no}</i><b>${esc(g.title)}</b></div>
      ${g.krs
        .map((k) => {
          const c = d.krs[k.krId] ?? { on: false, progress: k.progress, status: k.status, version: k.version };
          return `<div class="rp-kr ${c.on ? 'on' : ''}">
          <label class="rp-krh">
            <input type="checkbox" data-rpk="${esc(k.krId)}" ${c.on ? 'checked' : ''}
              aria-label="本期 ${esc(k.title)} 有进展">
            <span class="rp-krn">KR${k.no}　${esc(k.title)}</span>
            <span class="rp-krc">当前 <b class="num">${k.progress}%</b> · ${esc(KRST[k.status])}</span>
          </label>
          ${
            c.on
              ? `<div class="rp-krf">
            <label>拟更新进度
              <input type="number" min="0" max="100" step="5" value="${c.progress}" data-rpp="${esc(k.krId)}"
                aria-label="${esc(k.title)} 拟更新进度（0—100）"></label>
            <label>拟更新状态
              <select data-rps="${esc(k.krId)}" aria-label="${esc(k.title)} 拟更新状态">${KRST_KEYS.map(
                (x) => `<option value="${x}"${x === c.status ? ' selected' : ''}>${KRST[x]}</option>`,
              ).join('')}</select></label>
            <s data-rpdiff="${esc(k.krId)}">${esc(krDiff(k, c))}</s>
          </div>`
              : ''
          }
        </div>`;
        })
        .join('')}
    </div>`,
    )
    .join('');
}

export function editHtml(v: ReportDetailView, d: Draft): string {
  const done = isSubmitted(v);
  const mode = done ? '修订' : v.state === 'ended' ? '补交' : '填写';
  const btn = done ? '保存修改' : v.state === 'ended' ? '补交' + v.period.name : '提交双周报';
  const PH: Record<string, string> = {
    progress: '写清本期实际完成了什么、拿到了什么结果',
    plan: '写清下一期要推进的事项',
    risk: '本期无',
    need: '本期无',
  };

  return `
  <div class="crumb">
    <div class="crumb-path">
      <button data-rp="list">双周报</button><span>/</span>
      <button data-rp="list">${esc(v.period.name)}</button><span>/</span>
      <button data-rp="open|${esc(v.team.id)}">${esc(v.team.name)}</button><span>/</span><b>${mode}</b>
    </div>
    <div class="crumb-tools">
      <button class="navbtn" data-rp="cancel">取消（Esc）</button>
    </div>
  </div>

  <div class="page rp-form">
    ${headHtml(v, true)}
    ${when(
      v.state === 'ended',
      `<div class="note bad rp-warn">${IC.warn}<span>${esc(v.period.name)}已于 ${esc(
        v.period.end,
      )} 结束，本次为<b>逾期 ${v.owedDays} 天补交</b>。提交后状态显示为「已提交」，逾期时长保留在提交记录中。</span></div>`,
    )}
    <div class="rp-dirty" id="rp-dirty">${IC.info}<span>有修改的内容未保存，请注意及时保存</span></div>
    <p class="rp-note">${
      v.state === 'ended'
        ? '历史期补交与按期提交走同一套流程，唯一差别是不同步 KR 进度。'
        : `本期截止 ${esc(v.period.due)}${
            v.daysToDue === null ? '' : v.daysToDue === 0 ? '（今天截止）' : `（距截止还有 ${v.daysToDue} 天）`
          }。截止时间即周期结束当日 24:00，周期内提交均按时；截止后仍可补交，系统保留逾期标识与时长。`
    }提交后本队成员仍可继续编辑。</p>

    <div class="rp-card">
      <div class="rp-card-h"><b>${esc(v.period.name)}正文</b><em>已提交后四段内容一律跨队公开</em></div>
      ${SECS.map(
        ([k, lab]) => `
      <div class="rp-sec" id="rp-sec-${k}">
        <div class="rp-lab"><span>${lab}</span>${
          k === 'progress' || k === 'plan' ? '<span class="req">*</span>' : '<span class="opt">选填</span>'
        }
          <span class="cnt${d[k].length >= SEC_MAX ? ' full' : ''}" id="rp-cnt-${k}"><b class="num">${
            d[k].length
          }</b> / ${SEC_MAX} 字</span></div>
        <textarea data-rpf="${k}" maxlength="${SEC_MAX}" placeholder="${esc(PH[k] ?? '')}">${esc(d[k])}</textarea>
        <div class="err"></div>
      </div>`,
      ).join('')}
    </div>

    ${
      v.canSyncKr
        ? `<div class="rp-card">
      <div class="rp-card-h"><b>本期有进展的 KR</b><em>勾选后可填写拟更新进度与状态，提交时同步进团队 OKR</em></div>
      <div id="rp-krs">${krsHtml(v, d)}</div>
    </div>`
        : `<p class="rp-nokr"><b>历史期补交不同步 KR 进度。</b>KR 同步的含义是「把关键结果的<b>当前值</b>更新为拟更新值」，而${esc(
            v.period.name,
          )}已于 ${esc(
            v.period.end,
          )} 结束——用两周前的进度覆盖今天的值会造成进度倒退。如需更新关键结果，请前往 <button class="tbtn" data-rp="gokr">团队 OKR</button> 维护。</p>`
    }

    <div class="toolbar" style="margin-top:16px">
      <button class="btn primary" id="rp-main-btn" data-rp="submit" disabled>${esc(btn)}</button>
      <button class="btn" data-rp="cancel">取消</button>
    </div>
    ${metaHtml(done ? v.report : null)}
  </div>`;
}

/* ===================== 写操作 ===================== */

/** 主按钮可点状态与「有未保存的修改」提示**只改属性，不重渲染**——重渲染会打断正在输入的光标 */
function syncDirty(): void {
  const d = isDirty();
  const btn = $<HTMLButtonElement>('#rp-main-btn');
  if (btn) btn.disabled = !d;
  $('#rp-dirty')?.classList.toggle('on', d);
}

/**
 * 四段正文随内容自增高：默认两行（68px），写多少长多少。
 * ⚠ 先把高度压成 `0px` 再量 `scrollHeight`，**不要用一期那句 `height='auto'`**：
 *   `auto` 时框体会被容器撑开，量到的是"它现在占了多高"而不是"内容有多高"。
 *   表现是刚进编辑页时四个空输入框一个比一个高（480 / 285 / 91 / 91px）——
 *   联调时肉眼看见的，`npm run check` 一条都测不出来。
 */
function grow(ta: HTMLTextAreaElement): void {
  ta.style.height = '0px';
  ta.style.height = Math.max(68, ta.scrollHeight || 0) + 'px';
}

/** 必填两段的就地校验。⚠ 服务端同样会校，这里只是少一次往返 */
function validate(d: Draft): boolean {
  document.querySelectorAll('.rp-sec').forEach((s) => s.classList.remove('bad'));
  let first: HTMLElement | null = null;
  for (const [k, lab] of [
    ['progress', '本期进展与成果'],
    ['plan', '下期计划'],
  ] as const) {
    if (d[k].trim()) continue;
    const sec = $(`#rp-sec-${k}`);
    if (!sec) continue;
    sec.classList.add('bad');
    const e = sec.querySelector('.err');
    if (e) e.textContent = '请填写' + lab;
    if (!first) first = sec;
  }
  if (first) {
    first.scrollIntoView({ block: 'center' });
    first.querySelector('textarea')?.focus();
    return false;
  }
  return true;
}

/** 勾选了的 KR → 提交入参。⚠ 历史期草稿里恒为空对象，天然不会带 krSync */
function pickedKrs(d: Draft): KrProgressSyncItem[] {
  return Object.entries(d.krs)
    .filter(([, c]) => c.on)
    .map(([krId, c]) => ({
      krId,
      toProgress: c.progress,
      toStatus: c.status,
      // 🔴 打开编辑页那一版，不是最新值——它就是乐观锁的比对依据
      version: c.version,
    }));
}

let busy = false;

async function commit(v: ReportDetailView, d: Draft): Promise<void> {
  if (busy) return;
  busy = true;
  /* ⚠ 当前期【一律带上这个字段，哪怕是空数组】：服务端按「带没带」决定要不要重写同步记录，
     不带 = 这次不动 KR 关联（只改正文），空数组 = 把勾全取消了。两者不是一回事。 */
  const items = v.canSyncKr ? pickedKrs(d) : undefined;
  const input = {
    teamId: v.team.id,
    periodId: v.period.id,
    progress: d.progress.trim(),
    plan: d.plan.trim(),
    // ⚠ 留空由**服务端**补「本期无。」，前端不抢着补——两处各补一次，改口径时只改得动一处
    risk: d.risk.trim(),
    need: d.need.trim(),
    ...(items ? { krSync: items } : {}),
  };
  const done = isSubmitted(v);
  try {
    /* ⚠ 走 submit 还是 save 看的是 **status**：库里那条 `unsubmitted` 的行（seed 会造，
       三期草稿更是常态）**首次提交仍要走 submit** —— 服务端在那条路径上才会写
       submitter / submitAt / 逾期快照。 */
    const saved = done ? await api.save(v.report!.id, input) : await api.submit(input);
    const n = saved.krSync.length;
    setDraft(null); // ⚠ 先清草稿再跳转，否则会被离开拦截拦下来
    toast(
      done
        ? '已更新已提交内容，状态保持已提交'
        : `已${v.state === 'ended' ? '补交' : '提交'} ${v.team.code} ${v.team.name} 的${v.period.name}双周报` +
            (n ? `，并同步 ${n} 条关键结果` : ''),
    );
    /* 提交 / 保存后落到【详情页】：提交完通常要确认内容对不对，不是回列表 */
    go(`#/reports/p${v.period.no}/${v.team.id}`);
  } catch (e) {
    /* 🔴 版本冲突用 OKR 页那个弹窗（`krConflictModal`）——出参形状完全一致，
       **不要写第二个**。它关闭时会 refreshCurrent()，草稿仍在（同队同期不重建）。 */
    if (e instanceof ApiClientError && e.code === 'VERSION_CONFLICT') krConflictModal(e);
    else toast(errText(e));
  } finally {
    busy = false;
  }
}

function submitFlow(v: ReportDetailView, d: Draft): void {
  if (!validate(d)) return;

  /* 已提交后的修订是可逆的日常动作，不再二次确认；首次提交单向不可逆，必须确认 */
  if (isSubmitted(v)) {
    void commit(v, d);
    return;
  }

  const n = v.canSyncKr ? pickedKrs(d).length : 0;
  const late = v.state === 'ended';
  confirmModal({
    title: (late ? '补交' : '提交') + v.period.name + '双周报',
    okText: late ? '确认补交' : '确认提交',
    html:
      (late
        ? `${esc(v.period.name)}已于 <b>${esc(v.period.end)}</b> 结束，本次为<b>逾期 ${v.owedDays} 天补交</b>。<br>`
        : '') +
      `提交后该期双周报即刻对全体成员公开，且<b>不支持撤回或重新提交</b>（提交后仍可继续编辑）。<br>` +
      (v.canSyncKr
        ? n
          ? `将同步 <b>${n}</b> 条关键结果的进度与状态到团队 OKR。`
          : '本次没有勾选任何关键结果，不会同步 OKR 数据。'
        : '本期为历史期补交，<b>不同步 KR 进度</b>。') +
      (late ? '<br>提交后状态显示为「已提交」，逾期时长保留在提交记录中，不作为状态标识展示。' : ''),
    onOk: () => void commit(v, d),
  });
}

/* ===================== 页面模块 ===================== */

export interface ReportsData {
  list: ReportListView | null;
  detail: ReportDetailView | null;
  draft: Draft | null;
  /**
   * 周期切换器的选项 —— 来自 `shared/meta.ts` 的缓存，在 load() 里取好放进 data。
   * ⚠ 放进 data 而不是在 html() 里直接读 meta，是为了让 **html() 保持纯函数**
   *   （`pages/types.ts` 纪律 1：那是唯一能不起浏览器就测的部分）。
   */
  periods: Period[];
  /** 服务端「今天」，只用来给下拉选项标三态。⚠ 不许 new Date() */
  today: string;
  /** 只决定要不要显示「去管理后台 / 去队伍管理」入口。⚠ 权限的真相在服务端 */
  isAdmin: boolean;
  /** 未开始的期的空态里那个「回到第 N 期」按钮；没有可回的期为 null */
  backNo: number | null;
}

/** 离开编辑页前的二次确认（一期 rpGuard）。⚠ 只拦编辑态，列表与详情是纯只读 */
function installLeaveGuard(v: ReportDetailView): void {
  setLeaveGuard((proceed) => {
    if (!isDirty()) {
      setDraft(null);
      return proceed();
    }
    confirmModal({
      title: isSubmitted(v) ? '放弃未保存的修改' : '放弃已填写的内容',
      okText: '放弃并离开',
      cancelText: '留下继续填写',
      danger: true,
      html: isSubmitted(v)
        ? '这份双周报已提交，但本次修改尚未保存，离开后这些改动将丢失。'
        : '这份双周报尚未提交，离开后已填写的内容将丢失。',
      onOk: () => {
        setDraft(null);
        proceed();
      },
    });
  });
}

export const reportsPage = definePage<ReportsData>({
  skeleton: 'table',

  async load({ route, signal }) {
    if (route.view !== 'reports') throw new Error('[reports] 路由类型不符');
    const no = route.no ?? state.rp.no;

    /* ⚠ 这四样取自 meta 缓存（周期列表 / 服务端今天 / 是不是管理员），
       在 load() 里一次取好放进 data，html() 就仍然是纯函数。 */
    const env = {
      periods: meta.allPeriods(),
      today: meta.today(),
      isAdmin: meta.isAdmin(),
      backNo: meta.defaultPeriodNo(),
    };

    /* 列表页：期号可能是 null（系统里一期都还没建），服务端会返回空态而不是报错 */
    if (!route.team) {
      const list = await api.list(no, { signal });
      return { list, detail: null, draft: null, ...env };
    }

    const detail = await api.detail(route.team, no as number, { signal });
    /* ⚠ 不能编辑的对象（别队 / 归档队 / 未开始的期）**即使 hash 上带了 `/edit` 也不给表单**。
       路由守卫是同步的，判不了这一条——「能不能编辑」要打接口才知道（别队成员、归档状态）。
       所以这一层只能在这里做，`mounted` 里再把 hash 改回详情页，保持 hash 与界面一致。
       ⚠ 这是**别让人白填一屏**，不是权限本身：真正的拦截在服务端（红线 4）。 */
    if (!route.edit || !detail.canEdit) return { list: null, detail, draft: null, ...env };

    /* ⚠ 换队或换期才重建草稿；同一支队同一期**原地刷新不重建**——
       否则「打开 → 去 OKR 改了 KR → 回来提交」这条版本冲突路径永远走不出来
       （一期 2026-08-04 定，二期版本冲突弹窗的「刷新并重新核对」也依赖它）。 */
    const key = draftKey(detail.team.id, detail.period.no);
    if (state.rp.dkey !== key || !draftOf()) setDraft(newDraft(detail));
    return { list: null, detail, draft: draftOf(), ...env };
  },

  html(d) {
    if (d.list) return listHtml(d.list, d);
    if (d.detail && d.draft) return editHtml(d.detail, d.draft);
    if (d.detail) return detailHtml(d.detail, d);
    return emptyHtml(EIC.doc, '双周报暂时无法显示', '请刷新页面重试');
  },

  mounted(d, route) {
    /* hash 上带着 /edit 却没建出草稿 = 上面那条「不能编辑就不给表单」生效了 →
       把 hash 改回详情页，否则地址栏说在编辑、界面却是只读（router/guards.ts 的约定 2） */
    if (route.view === 'reports' && route.edit && d.detail && !d.draft) {
      go(`#/reports/p${d.detail.period.no}/${d.detail.team.id}`);
      return;
    }
    if (d.detail && d.draft) {
      // 四段正文进页就按内容撑开，不然回来修订时全是两行高、看不到写过什么
      document.querySelectorAll<HTMLTextAreaElement>('.rp-sec textarea').forEach(grow);
      syncDirty();
      installLeaveGuard(d.detail);
      return;
    }
    // ⚠ 列表 / 详情是只读页，**不设离开拦截**（render 每次渲染前已清空）
    const panel = $('#rp-panel');
    if (panel && state.rp.scroll) panel.scrollTop = state.rp.scroll;
  },

  onEvent(e, d) {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const v = d.detail;
    const draft = d.draft;

    /* ---- 编辑页的输入：只改草稿、不重渲染，否则会打断正在输入的光标 ---- */
    if (draft && v) {
      if (e.type === 'input' && t instanceof HTMLTextAreaElement && t.dataset.rpf) {
        const k = t.dataset.rpf as 'progress' | 'plan' | 'risk' | 'need';
        draft[k] = t.value;
        const c = $(`#rp-cnt-${k}`);
        if (c) {
          const n = c.querySelector('.num');
          if (n) n.textContent = String(t.value.length);
          c.classList.toggle('full', t.value.length >= SEC_MAX);
        }
        if (t.value.trim()) $(`#rp-sec-${k}`)?.classList.remove('bad');
        grow(t);
        syncDirty();
        return;
      }
      if (e.type === 'input' && t instanceof HTMLInputElement && t.dataset.rpp) {
        const c = draft.krs[t.dataset.rpp];
        if (c) {
          c.progress = Number(t.value);
          refreshDiff(v, draft, t.dataset.rpp);
          syncDirty();
        }
        return;
      }
      if (e.type === 'change' && t instanceof HTMLSelectElement && t.dataset.rps) {
        const c = draft.krs[t.dataset.rps];
        if (c) {
          c.status = t.value as KrStatus;
          refreshDiff(v, draft, t.dataset.rps);
          syncDirty();
        }
        return;
      }
      /* 勾选 / 取消要展开或收起两个控件 → **只重渲染 KR 区**，然后把焦点还给该复选框 */
      if (e.type === 'change' && t instanceof HTMLInputElement && t.dataset.rpk) {
        const id = t.dataset.rpk;
        const c = draft.krs[id];
        if (c) {
          c.on = t.checked;
          const box = $('#rp-krs');
          if (box) {
            box.innerHTML = krsHtml(v, draft);
            box.querySelector<HTMLElement>(`[data-rpk="${CSS.escape(id)}"]`)?.focus();
          }
          syncDirty();
        }
        return;
      }
    }

    /* ---- 周期切换器：走 hash（刷新与后退才回得到原期） ---- */
    if (e.type === 'change' && t instanceof HTMLSelectElement && t.id === 'rp-cycsel') {
      const sfx = t.dataset.rpsfx;
      go('#/reports/p' + t.value + (sfx ? '/' + sfx : ''));
      return;
    }

    /* ---- Esc：编辑页 = 取消（回上一层），详情页 = 回列表；列表页没有上一层 ---- */
    if (e instanceof KeyboardEvent && e.type === 'keydown' && e.key === 'Escape' && v) {
      e.preventDefault();
      handle(d, draft ? 'cancel' : 'list');
      return;
    }

    /* ---- 列表行键盘可达：Enter / 空格进详情，与矩阵行同一套 ---- */
    if (e instanceof KeyboardEvent && e.type === 'keydown' && (e.key === 'Enter' || e.key === ' ')) {
      const row = t.closest<HTMLElement>('tr[data-rp]');
      if (row?.dataset.rp) {
        e.preventDefault();
        handle(d, row.dataset.rp);
      }
      return;
    }

    if (e.type !== 'click') return;
    state.rp.scroll = $('#rp-panel')?.scrollTop ?? state.rp.scroll;
    const btn = t.closest<HTMLElement>('[data-rp]');
    if (!btn || (btn as HTMLButtonElement).disabled) return;
    handle(d, btn.dataset.rp as string);
  },
});

function refreshDiff(v: ReportDetailView, d: Draft, krId: string): void {
  const k = v.krs.find((x) => x.krId === krId);
  const c = d.krs[krId];
  const el = document.querySelector<HTMLElement>(`[data-rpdiff="${CSS.escape(krId)}"]`);
  if (k && c && el) el.textContent = krDiff(k, c);
}

/**
 * 三页所有点击交互的统一入口：`data-rp="动作|参数…"`。
 * ⚠ 跳转一律走 `go()` —— 离开拦截挂在它里面（`router/index.ts`），
 *   在这里直接改 `location.hash` 会绕过未保存内容的二次确认。
 */
function handle(d: ReportsData, payload: string): void {
  const [op, a1, a2] = String(payload || '').split('|');
  const no = d.detail?.period.no ?? d.list?.period?.no ?? state.rp.no;
  const teamId = d.detail?.team.id ?? '';

  if (op === 'cyc') {
    if (!a1) return;
    go('#/reports/p' + a1 + (a2 ? '/' + a2 : ''));
    return;
  }
  if (op === 'list') return go('#/reports/p' + no);
  if (op === 'open' && a1) return go(`#/reports/p${no}/${a1}`);
  if (op === 'team' && a1) return go(`#/reports/p${no}/${a1}`);
  if (op === 'edit' && a1) return go(`#/reports/p${no}/${a1}/edit`);
  /* 取消：已提交的回详情页，未提交的回列表（详情页只会显示一个空态，没意义） */
  if (op === 'cancel') {
    return go(d.detail && isSubmitted(d.detail) ? `#/reports/p${no}/${teamId}` : '#/reports/p' + no);
  }
  if (op === 'gokr') return go('#/okr/' + teamId);
  if (op === 'submit' && d.detail && d.draft) return submitFlow(d.detail, d.draft);
}

/* ⚠ 写完双周报**不调 `meta.refresh()`**（与队伍 / 周期的写操作不同）：
   提交不改变「有哪些队伍」「有哪些周期」，路由守卫的判定用不到它。
   ⚠ 但**队伍详情页的「本期双周报」区块**读的是自己的接口，不是 meta 缓存，
     所以那一块会自动是新的，不需要在这里做任何事。 */
